import React, { useState, useEffect, useRef } from "react";
import { Grid, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { IMaskInput } from "react-imask";
import getTrans from "../../../../../languages/getTrans";

const NumberMask = React.forwardRef(function NumberMask(props, ref) {
  const { onChange, name, ...other } = props;
  return (
    <IMaskInput
      {...other}
      mask={Number}
      scale={4}
      signed={false}
      radix="."
      mapToRadix={[',']}
      min={0}
      inputRef={ref}
      onAccept={(value) => {
        onChange({ target: { name, value: String(value || '') } });
      }}
    />
  );
});

const PaymentConverter = ({ paymentAmount, servicePayInfo, onChangeAmount }) => {
  const classes = useStyles();
  const [amountWith, setAmountWith] = useState('');
  const [amountWithout, setAmountWithout] = useState('');
  const [userIsTyping, setUserIsTyping] = useState(false);
  const timerRef = useRef(null);

  const toNumber = (value) => Number.parseFloat(value) || 0;
  const roundTo2 = (num) => {
    if (!num) return '';
    return num.toFixed(2);
  };
  const getCommission = () => {
    const percent = toNumber(servicePayInfo?.commissionPercentage);
    const fixed = toNumber(servicePayInfo?.commissionValue);
    return { percent, fixed };
  };
  const addCommission = (amount) => {
    const { percent, fixed } = getCommission();
    if (percent === 0) return amount + fixed;
    return (amount + fixed) / (1 - percent / 100);
  };
  const removeCommission = (amount) => {
    const { percent, fixed } = getCommission();
    return amount - (amount * percent / 100) - fixed;
  };
  const notifyParent = (without, with_) => {
    if (onChangeAmount) {
      onChangeAmount({
        newAmount: toNumber(roundTo2(without)),
        amountWithCommission: toNumber(roundTo2(with_))
      });
    }
  };
  const handleWithCommissionChange = (e) => {
    const value = e.target.value || '';
    setAmountWith(value);
    setUserIsTyping(true);

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const num = toNumber(value);
      if (num > 0) {
        const calculated = removeCommission(num);
        setAmountWith(roundTo2(num));
        setAmountWithout(roundTo2(calculated));
        notifyParent(calculated, num);
      } else {
        setAmountWithout('');
        notifyParent(0, 0);
      }
      setTimeout(() => setUserIsTyping(false), 200);
    }, 1000);
  };
  const handleWithoutCommissionChange = (e) => {
    const value = e.target.value || '';
    setAmountWithout(value);
    setUserIsTyping(true);

    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const num = toNumber(value);
      if (num > 0) {
        const calculated = addCommission(num);
        setAmountWithout(roundTo2(num));
        setAmountWith(roundTo2(calculated));
        notifyParent(num, calculated);
      } else {
        setAmountWith('');
        notifyParent(0, 0);
      }
      setTimeout(() => setUserIsTyping(false), 200);
    }, 1000);
  };

  useEffect(() => {
    if (userIsTyping) return;
    const num = toNumber(paymentAmount);
    if (num > 0) {
      setAmountWith(roundTo2(addCommission(num)));
      setAmountWithout(roundTo2(num));
    }
  }, [paymentAmount, userIsTyping]);
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <div className={classes.root}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <div className={classes.formItem}>
            <div className={classes.formItemTitle}>
              {getTrans('paymentConverter.Amount to be paid including commission')}, {servicePayInfo?.currency || 'USD'}:
            </div>
            <TextField
              fullWidth
              variant="outlined"
              value={amountWith}
              onChange={handleWithCommissionChange}
              className={classes.textField}
              InputProps={{
                inputComponent: NumberMask,
                inputProps: { name: 'amountWithCommission' }
              }}
            />
          </div>
        </Grid>
        <Grid item xs={12}>
          <div className={classes.formItem}>
            <div className={classes.formItemTitle}>
              {getTrans('paymentConverter.Will be credited')}, {servicePayInfo?.currency || 'USD'}:
            </div>
            <TextField
              fullWidth
              variant="outlined"
              value={amountWithout}
              onChange={handleWithoutCommissionChange}
              className={classes.textField}
              InputProps={{
                inputComponent: NumberMask,
                inputProps: { name: 'amountWithoutCommission' }
              }}
            />
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

const useStyles = makeStyles(() => ({
  root: {
    marginTop: 24
  },
  formItem: {},
  formItemTitle: {
    marginBottom: "8px",
    fontSize: 15,
    color: "#BDBDBD",
    lineHeight: "18px",
  },
  textField: {
    "& .MuiInputBase-root": {
      background: "#FFFFFF0D",
      border: "1px solid #FFFFFF1A",
    },
    "& .MuiOutlinedInput-input": {
      color: "white",
      cursor: "text",
    },
    "& .MuiInputBase-input": {
      cursor: "text",
    },
  },
}));

export default PaymentConverter;
